/** 할일 (PRD 6장): 입력 규칙과 응답 형태 */
import { z } from "zod";
import { isDateStr } from "./dates";
import { isValidRRule } from "./recur";

export const TASK_STATUSES = ["todo", "doing", "done"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const TASK_STATUS_NAMES: Record<TaskStatus, string> = { todo: "할일", doing: "진행중", done: "완료" };

/** 하위 할일 깊이 (최상위 = 0) */
export const MAX_TASK_DEPTH = 2;
/** 보드의 완료 칸에 보이는 기간 */
export const BOARD_DONE_DAYS = 14;

export const LABEL_COLORS = ["slate", "red", "orange", "amber", "green", "teal", "blue", "violet", "pink"] as const;
export type LabelColor = (typeof LABEL_COLORS)[number];

const id = z.number().int().positive();
export const taskStatusSchema = z.enum(TASK_STATUSES);
export const taskTitleSchema = z.string().trim().min(1, "제목을 입력하세요.").max(300, "제목은 300자까지입니다.");
export const dateStrSchema = z.string().refine(isDateStr, "날짜 형식이 아닙니다 (YYYY-MM-DD).");
export const timeStrSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "시각 형식이 아닙니다 (HH:MM).");
export const rruleSchema = z.string().max(200).refine(isValidRRule, "반복 규칙이 올바르지 않습니다.");
const prioritySchema = z.number().int().min(1).max(4);

export const createTaskInput = z.object({
  title: taskTitleSchema,
  description: z.string().max(20000, "설명은 20000자까지입니다.").default(""),
  /** 팀 할일이면 채널 */
  channelId: id.optional(),
  /** 개인 할일의 목록 (없으면 받은함) */
  listId: id.nullable().optional(),
  parentId: id.optional(),
  assigneeId: id.nullable().optional(),
  priority: prioritySchema.default(4),
  dueDate: dateStrSchema.nullable().optional(),
  dueTime: timeStrSchema.nullable().optional(),
  rrule: rruleSchema.nullable().optional(),
  repeatFromCompletion: z.boolean().optional(),
  labelIds: z.array(id).max(20).default([]),
  status: taskStatusSchema.optional(),
  /** 카드 메시지를 조용히 올린다 */
  silent: z.boolean().optional(),
  /** 이 메시지에서 만든 할일 */
  sourceMessageId: id.optional(),
  /** 위키 체크리스트 줄에서 만든 할일 (그 줄이 할일 줄이 된다) */
  wikiPageId: id.optional(),
});

export const updateTaskInput = z.object({
  title: taskTitleSchema.optional(),
  description: z.string().max(20000, "설명은 20000자까지입니다.").optional(),
  listId: id.nullable().optional(),
  assigneeId: id.nullable().optional(),
  priority: prioritySchema.optional(),
  dueDate: dateStrSchema.nullable().optional(),
  dueTime: timeStrSchema.nullable().optional(),
  rrule: rruleSchema.nullable().optional(),
  repeatFromCompletion: z.boolean().optional(),
  labelIds: z.array(id).max(20).optional(),
  status: taskStatusSchema.optional(),
  /** 같은 칸 안에서의 순서 (끌어서 옮길 때) */
  position: z.number().finite().optional(),
  /** 개인 할일을 채널로 옮겨 팀 할일로 (반대는 불가) */
  channelId: id.optional(),
  silent: z.boolean().optional(),
});

export const REMINDER_KINDS = ["before", "day_before", "at"] as const;
export type ReminderKind = (typeof REMINDER_KINDS)[number];

export const reminderInput = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("before"), offsetMinutes: z.number().int().min(0).max(60 * 24 * 7) }),
  z.object({ kind: z.literal("day_before"), daysBefore: z.number().int().min(0).max(30), time: timeStrSchema }),
  z.object({ kind: z.literal("at"), at: z.iso.datetime() }),
]);
export type ReminderInput = z.infer<typeof reminderInput>;
export const setRemindersInput = z.object({
  reminders: z.array(reminderInput).max(10, "리마인더는 10개까지입니다."),
  /** 방해 금지 시간에도 이 할일의 리마인더는 보낸다 */
  bypassQuiet: z.boolean().optional(),
});
export const reminderActionInput = z.object({ action: z.enum(["complete", "snooze", "tomorrow"]) });

export const labelNameSchema = z
  .string()
  .trim()
  .transform((s) => s.replace(/^\+/, ""))
  .pipe(z.string().min(1, "라벨 이름을 입력하세요.").max(30, "라벨 이름은 30자까지입니다.").regex(/^[^\s#@+!]+$/, "라벨 이름에는 공백과 # @ + !를 쓸 수 없습니다."));
export const createLabelInput = z.object({ name: labelNameSchema, scope: z.enum(["shared", "personal"]), color: z.enum(LABEL_COLORS).default("slate") });
export const updateLabelInput = z.object({ name: labelNameSchema.optional(), color: z.enum(LABEL_COLORS).optional() });

export const listNameSchema = z
  .string()
  .trim()
  .transform((s) => s.replace(/^#/, ""))
  .pipe(z.string().min(1, "목록 이름을 입력하세요.").max(40, "목록 이름은 40자까지입니다.").regex(/^[^\s#@+!]+$/, "목록 이름에는 공백과 # @ + !를 쓸 수 없습니다. 빠른 입력에서 #이름으로 찾기 때문입니다."));
export const createListInput = z.object({ name: listNameSchema });
export const updateListInput = z.object({ name: listNameSchema.optional(), position: z.number().finite().optional() });

/** 마감 조건 */
export const DUE_FILTERS = ["overdue", "today", "through_today", "this_week", "next_7", "has_date", "none"] as const;
export type DueFilter = (typeof DUE_FILTERS)[number];

/**
 * 할일 찾기 조건 (저장한 필터, 라벨 모아보기, 오늘·다가오는 화면이 모두 이걸로 찾는다)
 * scope: mine = 내 개인 할일 + 나에게 맡겨진 팀 할일, all = 내 개인 할일 + 모든 팀 할일
 */
export const taskFilterSchema = z.object({
  scope: z.enum(["mine", "all"]).default("mine"),
  /** 개인 목록 ("inbox" = 받은함) */
  lists: z.array(z.union([id, z.literal("inbox")])).max(50).optional(),
  channelIds: z.array(id).max(50).optional(),
  labelIds: z.array(id).max(50).optional(),
  /** 담당자 ("me", "none", 또는 사람 id) */
  assignees: z.array(z.union([id, z.literal("me"), z.literal("none")])).max(50).optional(),
  due: z.enum(DUE_FILTERS).optional(),
  priorities: z.array(prioritySchema).max(4).optional(),
  recurring: z.boolean().optional(),
});
export type TaskFilter = z.infer<typeof taskFilterSchema>;

/** 개인 화면(받은함·오늘·다가오는)의 조건. 서버가 첫 화면 데이터를 미리 담을 때도 같은 값을 쓴다 */
export const VIEW_FILTERS = {
  inbox: { scope: "mine", lists: ["inbox"] },
  today: { scope: "mine", due: "through_today" },
  upcoming: { scope: "mine", due: "has_date" },
} satisfies Record<string, TaskFilter>;
export const searchTasksInput = taskFilterSchema.extend({ includeDone: z.boolean().optional() });

export const filterNameSchema = z.string().trim().min(1, "필터 이름을 입력하세요.").max(40, "필터 이름은 40자까지입니다.");
export const createFilterInput = z.object({ name: filterNameSchema, conditions: taskFilterSchema });
export const updateFilterInput = z.object({ name: filterNameSchema.optional(), conditions: taskFilterSchema.optional() });

export const defaultReminderInput = z.object({ minutes: z.number().int().min(0).max(60 * 24).nullable() });

/* ───────────── 응답 ───────────── */

export interface TaskDTO {
  /** T-번호 */
  id: number;
  channelId: number | null;
  /** 개인 할일이면 소유자 */
  ownerId: number | null;
  listId: number | null;
  parentId: number | null;
  title: string;
  description: string;
  status: TaskStatus;
  assigneeId: number | null;
  creatorId: number | null;
  priority: number;
  dueDate: string | null;
  dueTime: string | null;
  rrule: string | null;
  repeatFromCompletion: boolean;
  position: number;
  labelIds: number[];
  sourceMessageId: number | null;
  cardMessageId: number | null;
  subtaskCount: number;
  subtaskDone: number;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

/** 채널의 할일 카드 메시지에 실리는 요약 */
export type TaskCardDTO = Pick<TaskDTO, "id" | "title" | "status" | "assigneeId" | "dueDate" | "dueTime" | "priority" | "rrule" | "repeatFromCompletion" | "subtaskCount" | "subtaskDone" | "deleted">;

/** `#T-123` 칩에 보여줄 요약 (볼 수 없는 할일은 응답에서 빠진다) */
export type TaskRefDTO = TaskCardDTO & Pick<TaskDTO, "channelId">;
/** 한 번에 물어볼 수 있는 할일 수 */
export const MAX_TASK_REFS = 100;

export interface ReminderDTO {
  id: number;
  kind: ReminderKind;
  offsetMinutes: number | null;
  daysBefore: number | null;
  time: string | null;
  at: string | null;
  /** 보낼 시각 (마감 시각이 없으면 null) */
  fireAt: string | null;
  sent: boolean;
  isDefault: boolean;
  bypassQuiet: boolean;
}

export interface TaskDetailDTO {
  task: TaskDTO;
  /** 하위 할일 (하위의 하위 포함, 순서대로) */
  subtasks: TaskDTO[];
  /** 상위 할일 (가까운 순) */
  ancestors: { id: number; title: string }[];
  /** 내 리마인더 */
  reminders: ReminderDTO[];
  /** 최근 완료 기록 (반복 할일) */
  completions: { userId: number | null; dueDate: string | null; completedAt: string }[];
  /** 출처 메시지 */
  source: { messageId: number; channelId: number; authorId: number | null; preview: string } | null;
  /** 이 할일이 할일 줄로 들어 있는 위키 페이지 (볼 수 있는 것만) */
  wikiPages: { pageId: number; title: string; channelId: number | null }[];
}

export interface LabelDTO {
  id: number;
  scope: "shared" | "personal";
  name: string;
  color: LabelColor;
}

export interface LabelEventDTO {
  id: number;
  labelId: number | null;
  actorId: number | null;
  action: "create" | "rename" | "recolor" | "delete";
  name: string;
  before: string | null;
  createdAt: string;
}

export interface PersonalListDTO {
  id: number;
  name: string;
  position: number;
  /** 끝나지 않은 할일 수 */
  openCount: number;
}

export interface SavedFilterDTO {
  id: number;
  name: string;
  conditions: TaskFilter;
  position: number;
}

export interface BoardDTO {
  /** 끝나지 않은 할일 + 최근 14일 안에 끝낸 할일 (하위 할일 포함) */
  tasks: TaskDTO[];
}

export interface DoneHistoryPage {
  tasks: TaskDTO[];
  hasMore: boolean;
}
